// CaseLedger — the single assembly authority for the EBITDA composite answer.
//
// One structure, computed ONCE per composite answer (in the API from the
// CompositeEbitdaPicture), that every rendering surface BINDS. Its job is to end
// the class of failure the EBITDA numeric adjudication found: the same per-lever
// atoms re-summed by multiple composers with different inclusion rules and
// different denominators, producing a 20.3% / 110% / 119% / 135% "coverage" swing
// for the identical ask.
//
// Two load-bearing invariants:
//   1. ONE denominator. `denominatorUsd` (the ask's gap) is identical on every
//      case; every `coveragePct` divides by it. No surface may pick a second.
//   2. NEVER sum an undeclared / unmeasured pair. The ledger only sums members
//      whose pairwise disposition is disjoint (receipted or modeled). A
//      `measuring` or `undeclared` pair is rendered as an honest open line, never
//      collapsed into a clean total. This CONSUMES the deconfliction state the
//      aggregator / overlap registry already resolved (#3437 owns the fence); the
//      ledger never overrides a disposition or invents a receipt.
//
// Types only + pure guards. Runtime assembly (`buildCaseLedger`) lives in the API
// lane beside the aggregator, never here.

// Evidence class of a member — drives which case it first enters (proven floor,
// modeled best-estimate, or gated upside).
export type CaseLedgerEvidenceClass = 'proven' | 'modeled' | 'gated';

// Per-pair overlap disposition. CONSUMED from the deconfliction registry / picture,
// never decided here. `undeclared` = no registry declaration => fail closed: the
// pair is never summed, only surfaced as an open line.
export type CaseLedgerOverlapState =
  | 'disjoint_receipted' // APPROVED zero-overlap receipt on an actual GL decomposition
  | 'disjoint_modeled' // receipt rests on a MODELED GL decomposition (flagged honestly)
  | 'measuring' // measurement in flight (the open line; e.g. workforce :: org)
  | 'undeclared'; // no declaration => fail closed, never summed

export type CaseLedgerOverlapPair = Readonly<{
  leverA: string;
  leverB: string;
  state: CaseLedgerOverlapState;
  // Measured overlap dollar; null when the pair is open (measuring / undeclared).
  overlapUsd: number | null;
  // Honest one-line disposition (reader-facing, no system nouns).
  note: string;
}>;

export type CaseLedgerMember = Readonly<{
  leverFamilyId: string;
  label: string;
  // Byte-identical to the picture's per-lever governed value — never recomputed.
  amountUsd: number;
  evidenceClass: CaseLedgerEvidenceClass;
}>;

// Producer-published package composition. This is deliberately distinct from
// `CaseLedgerCase.members`: members are lever families, while categories are
// the de-conflicted executive rollups that may group one or more proven levers.
// A renderer must never infer categories from member count, order, or labels.
export type CaseLedgerPackageCategory = Readonly<{
  categoryId: string;
  label: string;
  amountUsd: number;
  memberLeverFamilyIds: readonly string[];
  evidenceClass: 'proven';
  deconflictionStatus: 'deconflicted';
}>;

export const CASE_LEDGER_PACKAGE_SELECTION_POLICIES = [
  'overlap_resolved_bridge_v1',
  'risk_adjusted_target_fill_v1',
] as const;

export type CaseLedgerPackageSelectionPolicy =
  (typeof CASE_LEDGER_PACKAGE_SELECTION_POLICIES)[number];

export const CASE_LEDGER_PACKAGE_AMOUNT_VALUE_PATH =
  'decisionViewFacts.factPacket.facts.provenDisjointLeverAmountUsd' as const;

export type CaseLedgerPackageMemberEvidence = Readonly<{
  leverFamilyId: string;
  evidenceRefId: string;
}>;

// The producer must bind the package to the exact composite packet, closed
// selection policy, reporting cut, and one governed evidence reference per
// proven member. Renderers may not substitute generic bridge proof refs.
export type CaseLedgerPackageAuthority = Readonly<{
  packetId: string;
  // Optional at the transport boundary so render-open consumers can label an
  // older/incomplete packet as lineage-pending. A complete producer packet and
  // the invariant guard below require a non-empty value.
  runId?: string | null;
  source: 'governed_composite_rollup';
  amountValuePath: typeof CASE_LEDGER_PACKAGE_AMOUNT_VALUE_PATH;
  selectionPolicy: CaseLedgerPackageSelectionPolicy;
  scope: string;
  timeframe: string;
  asOf: string;
  memberEvidence: readonly CaseLedgerPackageMemberEvidence[];
}>;

export type CaseLedgerPackageComposition = Readonly<{
  schemaVersion: 'ask_helix_package_composition_v1';
  basisCaseId: 'proven';
  authority: CaseLedgerPackageAuthority;
  categories: readonly CaseLedgerPackageCategory[];
}>;

export type CaseLedgerCaseId = 'proven' | 'best_estimate' | 'full_upside';

// Peg's ratified open-overlap grammar (peg-full-option-presentation-20260718.md §2)
// for a case whose clean point total is blocked by an unmeasured / undeclared pair
// — the full-upside third case. The case builds in full and then STOPS, visibly, on
// the one line that cannot yet be filled.
export type CaseLedgerOpenOverlap = Readonly<{
  // The standalone add that would enter if the overlap were zero (e.g. workforce).
  standaloneAddUsd: number;
  // The pair carrying the open line (state = measuring | undeclared).
  overlapPair: CaseLedgerOverlapPair;
  // The "measured precedent" chip ($1.5M) — a proven prior, not a booked number.
  measuredPrecedentUsd: number | null;
  // [DATE] from the measurement lane's ETA, else "measurement in progress".
  measurementEtaLabel: string | null;
  // The refused face-value add (best-estimate + standalone). NEVER a headline;
  // it appears only inside the "add at face value" caution copy.
  naiveCeilingUsd: number;
}>;

export type CaseLedgerCase = Readonly<{
  caseId: CaseLedgerCaseId;
  // "Proven today" / "Best current estimate" / "Full upside — one line still open".
  label: string;
  // Cumulative members of this case (proven ⊆ best_estimate ⊆ full_upside).
  members: readonly CaseLedgerMember[];
  // Members added versus the case below (for the ascending-ladder render).
  addedMembers: readonly CaseLedgerMember[];
  // The member sum — the ONLY total for this case. Present only when every member
  // pair is disjoint; a case blocked by an open overlap carries a range instead.
  sumUsd: number;
  // The ONE denominator (the ask's gap) — identical on every case.
  denominatorUsd: number | null;
  // sumUsd / denominatorUsd. Honest; NOT capped at 100, NOT relabeled "certified".
  coveragePct: number | null;
  // denominatorUsd − sumUsd (the "still to close"), floored at zero.
  residualUsd: number | null;
  // Honest one-line posture ("This is the floor." …).
  postureLine: string;
  // Present only for a case blocked by an unmeasured / undeclared pair.
  openOverlap?: CaseLedgerOpenOverlap;
  // Full-upside bracket while the open line is unresolved: floor = the clean total
  // below it, ceiling = the naive add. Rendered "lands between … — below the top".
  rangeUsd?: Readonly<{ floorUsd: number; ceilingUsd: number }>;
}>;

// Why the ledger published NO case. A ledger with zero cases used to be an
// unlabelled empty shell: every consumer could tell that nothing resolved but
// none could tell WHY, so the only honest render left was an em dash. That is
// the failure this block ends — the authority now states its own reason, in the
// same place it states its cases, so a surface never has to guess or go find a
// second source. It carries NAMES AND REASONS ONLY, never a dollar: a withheld
// package must not become a back door for an unbound number.
export type CaseLedgerNoCaseReasonCode =
  // No lever returned an approved contribution to the roll-up at all.
  | 'no_governed_lever_contribution'
  // Levers did return contributions, but no receipt-backed mutually-disjoint
  // package can be formed from them (every candidate pair still needs an
  // overlap receipt, and no single lever is bankable on its own).
  | 'no_receipted_disjoint_package';

export type CaseLedgerNoCaseHeldLever = Readonly<{
  leverFamilyId: string;
  label: string;
  // The picture's OWN governed hold reason, copied verbatim. Never authored
  // here, never a dollar amount.
  reason: string;
}>;

export type CaseLedgerNoCaseReason = Readonly<{
  reasonCode: CaseLedgerNoCaseReasonCode;
  // One plain sentence a CFO can read in the first screen. No numbers.
  headline: string;
  // One follow-on sentence naming what would change the answer. No numbers.
  detail: string;
  // The levers that carried governed content and were held, each with its own
  // reason. Empty for `no_governed_lever_contribution`.
  heldLevers: readonly CaseLedgerNoCaseHeldLever[];
}>;

export type CaseLedger = Readonly<{
  ledgerVersion: 'ask_helix_case_ledger_v1';
  generatedAt: string;
  // The ONE denominator + its base naming (never blur two EBITDA/OI numbers).
  denominatorUsd: number | null; // the ask's gap
  baseMetricKey: string | null;
  baseValueUsd: number | null;
  targetPct: number | null;
  // Optional until the governed producer publishes an explicit composition.
  // Absence means the executive composition strip must withhold; consumers may
  // not reconstruct it from case members.
  packageComposition?: CaseLedgerPackageComposition;
  // [proven, best_estimate, full_upside] — ascending confidence ladder.
  cases: readonly CaseLedgerCase[];
  // Present EXACTLY when `cases` is empty: the authority's own stated reason for
  // publishing no case. A ledger may never carry both a case and a withhold.
  noCaseReason?: CaseLedgerNoCaseReason;
  // All visible pairwise overlap dispositions in scope.
  overlapPairs: readonly CaseLedgerOverlapPair[];
  // Peg's guardrail: "Do not add the cases together."
  doNotAddCasesNote: string;
  // SG&A conservatism footer (context, NOT case arithmetic), or null.
  contextFooter: string | null;
}>;

// --- Pure guards (shared by the producer and the cross-surface consistency test) ---

/** The single coverage formula. Every surface's coverage % must equal this. */
export function caseLedgerCoveragePct(
  sumUsd: number,
  denominatorUsd: number | null
): number | null {
  if (denominatorUsd == null || !(denominatorUsd > 0)) return null;
  return (sumUsd / denominatorUsd) * 100;
}

/** True when a pair may be summed (disjoint). Open/undeclared pairs fail closed. */
export function caseLedgerPairIsSummable(state: CaseLedgerOverlapState): boolean {
  return state === 'disjoint_receipted' || state === 'disjoint_modeled';
}

export function caseLedgerPackageSelectionPolicyIsRecognized(
  value: unknown
): value is CaseLedgerPackageSelectionPolicy {
  return CASE_LEDGER_PACKAGE_SELECTION_POLICIES.some((policy) => policy === value);
}

export type CaseLedgerInvariantViolation = Readonly<{
  code: string;
  detail: string;
}>;

/**
 * Fail-closed invariant check the producer and tests share:
 *   - every case divides by the SAME denominator;
 *   - every case's coveragePct equals caseLedgerCoveragePct(sumUsd, denominator);
 *   - no case summed a non-disjoint pair (a blocked case must carry openOverlap,
 *     not a clean coveragePct).
 * Returns [] when the ledger is internally consistent.
 */
export function caseLedgerInvariantViolations(ledger: CaseLedger): CaseLedgerInvariantViolation[] {
  const violations: CaseLedgerInvariantViolation[] = [];
  for (const kase of ledger.cases) {
    if (kase.denominatorUsd !== ledger.denominatorUsd) {
      violations.push({
        code: 'denominator_mismatch',
        detail: `case ${kase.caseId} denominator ${String(
          kase.denominatorUsd
        )} !== ledger denominator ${String(ledger.denominatorUsd)}`,
      });
    }
    const expectedPct = caseLedgerCoveragePct(kase.sumUsd, kase.denominatorUsd);
    if (!coveragePctMatches(kase.coveragePct, expectedPct)) {
      violations.push({
        code: 'coverage_pct_recomputed',
        detail: `case ${kase.caseId} coveragePct ${String(
          kase.coveragePct
        )} !== one-denominator coverage ${String(expectedPct)}`,
      });
    }
  }
  validateNoCaseReason(ledger, violations);
  validatePackageComposition(ledger, violations);
  return violations;
}

// A withhold and a published case are mutually exclusive states of the same
// authority. Allowing both would recreate the disagreement the ledger exists to
// prevent — one surface reading the case, another reading the reason it does not
// exist. The withhold also stays numeric-free: no dollar may enter the answer
// through the path that exists to explain their absence.
function validateNoCaseReason(
  ledger: CaseLedger,
  violations: CaseLedgerInvariantViolation[]
): void {
  // NOTE: an unlabelled empty ledger stays LEGAL here on purpose. This guard is
  // shared with the emission gate, and a violation there drops the whole ledger
  // prop; a pre-existing producer that has not been taught to state its reason
  // must not lose its (correct, empty) ledger as a side effect. The current
  // producer always states one, and its own spec asserts that.
  const noCaseReason = ledger.noCaseReason;
  if (!noCaseReason) return;
  if (ledger.cases.length > 0) {
    violations.push({
      code: 'withheld_with_published_case',
      detail: 'ledger carries a withhold reason while also publishing a case',
    });
  }
  if (!noCaseReason.headline.trim() || !noCaseReason.detail.trim()) {
    violations.push({
      code: 'withheld_reason_empty',
      detail: 'withhold headline or detail is empty',
    });
  }
  if (
    noCaseReason.reasonCode === 'no_governed_lever_contribution' &&
    noCaseReason.heldLevers.length > 0
  ) {
    violations.push({
      code: 'withheld_held_levers_contradict_reason',
      detail: 'reason says no lever contributed, but held levers are named',
    });
  }
  if (
    noCaseReason.reasonCode === 'no_receipted_disjoint_package' &&
    noCaseReason.heldLevers.length === 0
  ) {
    violations.push({
      code: 'withheld_held_levers_missing',
      detail: 'reason says a package could not be formed, but no lever is named',
    });
  }
  const seen = new Set<string>();
  for (const lever of noCaseReason.heldLevers) {
    if (!lever.leverFamilyId.trim() || !lever.label.trim() || !lever.reason.trim()) {
      violations.push({
        code: 'withheld_held_lever_invalid',
        detail: `held lever ${lever.leverFamilyId} is missing an id, label, or reason`,
      });
    }
    if (seen.has(lever.leverFamilyId)) {
      violations.push({
        code: 'withheld_held_lever_duplicated',
        detail: `held lever ${lever.leverFamilyId} is named more than once`,
      });
    }
    seen.add(lever.leverFamilyId);
    if (/\d/.test(lever.reason) && /[$%]/.test(lever.reason)) {
      violations.push({
        code: 'withheld_held_lever_carries_amount',
        detail: `held lever ${lever.leverFamilyId} reason carries a figure; the withhold is names and reasons only`,
      });
    }
  }
}

function validatePackageComposition(
  ledger: CaseLedger,
  violations: CaseLedgerInvariantViolation[]
): void {
  const composition = ledger.packageComposition;
  if (!composition) return;

  if (
    composition.schemaVersion !== 'ask_helix_package_composition_v1' ||
    composition.basisCaseId !== 'proven'
  ) {
    violations.push({
      code: 'package_composition_contract_invalid',
      detail: 'package composition schema or basis case is not recognized',
    });
    return;
  }

  const provenCase = ledger.cases.find((candidate) => candidate.caseId === 'proven');
  if (!provenCase) {
    violations.push({
      code: 'package_composition_missing_proven_case',
      detail: 'package composition declares the proven case, but no proven case exists',
    });
    return;
  }

  const authority = composition.authority;
  if (
    !authority ||
    !authority.packetId.trim() ||
    !authority.runId?.trim() ||
    authority.source !== 'governed_composite_rollup' ||
    authority.amountValuePath !== CASE_LEDGER_PACKAGE_AMOUNT_VALUE_PATH ||
    !caseLedgerPackageSelectionPolicyIsRecognized(authority.selectionPolicy) ||
    !authority.scope.trim() ||
    !authority.timeframe.trim() ||
    !authority.asOf.trim() ||
    authority.asOf !== ledger.generatedAt
  ) {
    violations.push({
      code: 'package_composition_authority_invalid',
      detail: 'package composition authority is missing, unrecognized, or stale',
    });
    return;
  }

  const authorityMemberIds = new Set<string>();
  const authorityEvidenceRefIds = new Set<string>();
  for (const memberEvidence of authority.memberEvidence) {
    const memberId = memberEvidence.leverFamilyId.trim();
    const evidenceRefId = memberEvidence.evidenceRefId.trim();
    if (
      !memberId ||
      !evidenceRefId ||
      authorityMemberIds.has(memberId) ||
      authorityEvidenceRefIds.has(evidenceRefId)
    ) {
      violations.push({
        code: 'package_composition_member_lineage_invalid',
        detail: `package member lineage or evidence is empty or duplicated for ${memberEvidence.leverFamilyId}`,
      });
    }
    authorityMemberIds.add(memberId);
    authorityEvidenceRefIds.add(evidenceRefId);
  }
  if (
    authorityMemberIds.size !== provenCase.members.length ||
    provenCase.members.some((member) => !authorityMemberIds.has(member.leverFamilyId))
  ) {
    violations.push({
      code: 'package_composition_member_lineage_incomplete',
      detail: 'package authority does not bind every proven member exactly once',
    });
  }

  if (composition.categories.length === 0) {
    violations.push({
      code: 'package_composition_empty',
      detail: 'package composition must publish at least one category',
    });
    return;
  }

  const provenMembersById = new Map<string, CaseLedgerMember>();
  for (const member of provenCase.members) {
    if (provenMembersById.has(member.leverFamilyId)) {
      violations.push({
        code: 'package_composition_duplicate_proven_member',
        detail: `proven case repeats lever family ${member.leverFamilyId}`,
      });
    }
    provenMembersById.set(member.leverFamilyId, member);
  }

  const categoryIds = new Set<string>();
  const coveredMemberIds = new Set<string>();
  let categoryTotalUsd = 0;

  for (const category of composition.categories) {
    const categoryId = category.categoryId.trim();
    if (!categoryId || categoryIds.has(categoryId)) {
      violations.push({
        code: 'package_composition_category_identity_invalid',
        detail: `package category identity is empty or duplicated: ${category.categoryId}`,
      });
    }
    categoryIds.add(categoryId);

    if (!category.label.trim()) {
      violations.push({
        code: 'package_composition_category_label_missing',
        detail: `package category ${category.categoryId} has no label`,
      });
    }
    if (category.evidenceClass !== 'proven' || category.deconflictionStatus !== 'deconflicted') {
      violations.push({
        code: 'package_composition_posture_invalid',
        detail: `package category ${category.categoryId} is not proven and de-conflicted`,
      });
    }
    if (!Number.isFinite(category.amountUsd) || category.amountUsd < 0) {
      violations.push({
        code: 'package_composition_category_amount_invalid',
        detail: `package category ${category.categoryId} amount is invalid`,
      });
    } else {
      categoryTotalUsd += category.amountUsd;
    }

    if (category.memberLeverFamilyIds.length === 0) {
      violations.push({
        code: 'package_composition_category_members_missing',
        detail: `package category ${category.categoryId} has no proven members`,
      });
      continue;
    }

    let expectedCategoryAmountUsd = 0;
    const categoryMemberIds = new Set<string>();
    for (const memberId of category.memberLeverFamilyIds) {
      const normalizedMemberId = memberId.trim();
      const member = provenMembersById.get(normalizedMemberId);
      if (
        !normalizedMemberId ||
        categoryMemberIds.has(normalizedMemberId) ||
        coveredMemberIds.has(normalizedMemberId)
      ) {
        violations.push({
          code: 'package_composition_member_duplicated',
          detail: `proven member ${memberId} is assigned more than once`,
        });
      }
      categoryMemberIds.add(normalizedMemberId);
      coveredMemberIds.add(normalizedMemberId);
      if (!member || member.evidenceClass !== 'proven') {
        violations.push({
          code: 'package_composition_member_not_proven',
          detail: `package category ${category.categoryId} references non-proven member ${memberId}`,
        });
        continue;
      }
      expectedCategoryAmountUsd += member.amountUsd;
    }

    if (!currencyAmountMatches(category.amountUsd, expectedCategoryAmountUsd)) {
      violations.push({
        code: 'package_composition_category_not_reconciled',
        detail: `package category ${category.categoryId} amount ${category.amountUsd} does not reconcile to its proven members ${expectedCategoryAmountUsd}`,
      });
    }
  }

  for (const memberId of provenMembersById.keys()) {
    if (!coveredMemberIds.has(memberId)) {
      violations.push({
        code: 'package_composition_member_omitted',
        detail: `proven member ${memberId} is absent from package composition`,
      });
    }
  }

  if (!currencyAmountMatches(categoryTotalUsd, provenCase.sumUsd)) {
    violations.push({
      code: 'package_composition_total_not_reconciled',
      detail: `package category total ${categoryTotalUsd} does not reconcile to proven case ${provenCase.sumUsd}`,
    });
  }
}

function coveragePctMatches(a: number | null, b: number | null): boolean {
  if (a == null || b == null) return a === b;
  return Math.abs(a - b) < 1e-6;
}

function currencyAmountMatches(a: number, b: number): boolean {
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 0.005;
}
